import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TicketType } from '../enums/ticketing.enum';
import { TicketReportFlowService } from '../services/ticket-report-flow.service';

/**
 * The "Feedback" launcher, one of the top bar's tools (bc-app-shell). It used to float in the
 * bottom-right corner, where it covered the page's own buttons.
 */
@Component({
  selector: 'app-report-widget',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './report-widget.component.html',
  styleUrls: ['./report-widget.component.scss'],
})
export class ReportWidgetComponent {
  private readonly flow = inject(TicketReportFlowService);
  private readonly router = inject(Router);
  private readonly host = inject(ElementRef<HTMLElement>);

  protected readonly menuOpen = signal(false);
  protected readonly busy = signal(false);

  protected readonly TicketType = TicketType;

  /** A click anywhere else closes the menu, as with the other top-bar menus. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen() && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen.set(false);
    }
  }

  /**
   * Ctrl+Shift+B. Bound on the document because the launcher is rarely focused — the point of a
   * shortcut here is to report a bug without first hunting for the button.
   */
  @HostListener('document:keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'b') {
      event.preventDefault();
      this.toggleMenu();
      return;
    }

    if (event.key === 'Escape' && this.menuOpen()) {
      this.menuOpen.set(false);
    }
  }

  protected toggleMenu(): void {
    if (this.busy()) return;
    this.menuOpen.update(open => !open);
  }

  /**
   * Starts the flow. The widget hides itself for the capture — otherwise the launcher appears in the
   * screenshot of the screen it was used to report — and the flow service owns the rest.
   */
  protected async start(type: TicketType): Promise<void> {
    this.menuOpen.set(false);
    this.busy.set(true);

    try {
      await this.flow.start({
        type,
        route: this.router.url,
        // The launcher and its menu, hidden during rasterising.
        hideDuringCapture: Array.from(
          document.querySelectorAll<HTMLElement>('app-report-widget .bct-launcher, app-report-widget .bct-menu'),
        ),
      });
    } finally {
      this.busy.set(false);
    }
  }
}
